import React, { useState, useEffect, useRef, Fragment, MouseEvent, useMemo, useLayoutEffect } from "react";
import { useTranslation } from "react-i18next";

//MUI
import Fab from "@mui/material/Fab";
import Paper from "@mui/material/Paper";
import Skeleton from "@mui/material/Skeleton";
import Tooltip from '@mui/material/Tooltip';

//MUI Icons
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import MapsHomeWorkIcon from '@mui/icons-material/MapsHomeWork';

//OL
import OlFormatWKT from "ol/format/WKT";

//Custom
import imgService from "@/services/imgService";
import Canvas from "./Canvas";

//Types
import { DCRecord } from "@/@types/lib/dataController";

export type Clip = [number, number, number, number];
type Ratio = [number, number];
type Pair<A, B> = [A, B];
type Coordinates = Pair<number, number>[] | null;
type Point = [number, number];

const PT_RADIUS = 5;
const MIN_WIDTH = 400;

export type ObjectAnalysisImageProps = {
  mouseUp: MouseEvent<HTMLDivElement> | undefined,
  baseRecordPath: string;
  baseRecordId: number | null;
  image: DCRecord | null;
  onDrawCropClip: (clip: Clip | null) => void;
  largeCanvas: boolean,
  setLargeCanvas: React.Dispatch<React.SetStateAction<boolean>>,
  drawCropClip: Clip | null;
  setIsDrawing: React.Dispatch<React.SetStateAction<boolean>>
}

const canvasParentPaddingPixels = 10;

const useWindowSize = () => {
  const [size, setSize] = useState([0, 0]);
  useLayoutEffect(() => {
    function updateSize() {
      setSize([window.innerWidth, window.innerHeight]);
    }
    window.addEventListener('resize', updateSize);
    updateSize();
    return () => window.removeEventListener('resize', updateSize);
  }, []);
  return size;
}

const ObjectAnalysisImage = (props: ObjectAnalysisImageProps) => {
  const { t } = useTranslation();

  const { mouseUp, baseRecordPath, baseRecordId, image, onDrawCropClip, drawCropClip, largeCanvas, setLargeCanvas, setIsDrawing } = props;

  const [zoomClip, setZoomClip] = useState<Clip | null>(null);
  const [cropClip, setCropClip] = useState<Clip | null>(null);
  const [b64, setB64] = useState<string>('');
  const [fullB64, setFullB64] = useState<string>('');
  const [initialB64, setInitialB64] = useState<string>('');
  const [previousCanvasState, setPreviousCanvasState] = useState(largeCanvas)
  const [isLoading, setIsLoading] = useState(false)

  const [viewOtherObjects, setViewOtherObjects] = useState<boolean>(false);
  const [otherObjects, setOtherObjects] = useState<DCRecord[]>([]);
  const [otherObjectsLoaded, setOtherObjectsLoaded] = useState(false);
  const [isOtherObjectsView, setIsOtherObjectsView] = useState<boolean>(false);

  const [windowWidth, windowHeight] = useWindowSize();

  const wrapperRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const canvasPolyRef = useRef<HTMLCanvasElement>(null);

  const imageId: number | null = image ? image.id as number : null;

  const imgAspectRatio = useMemo(()=> {
    if (image) {
      return `${image.width as number}/${image.height as number}`
    } 
    return '4/3'
  },[image])

  const [maxImageWidth, setMaxImageWidth] = useState(largeCanvas ? 800 : 600);
  const [maxImageHeight, setMaxImageHeight] = useState(largeCanvas ? 533 : 400);

  useLayoutEffect(() => {
    if (!wrapperRef.current) return;

    const recalc = () => {
      if (!wrapperRef.current) {
        setMaxImageWidth(largeCanvas ? 800 : 600);
        setMaxImageHeight(largeCanvas ? 533 : 400)
        return;
      }
      
      setMaxImageWidth(
        wrapperRef.current.clientWidth - 2 * canvasParentPaddingPixels
      );
      setMaxImageHeight(
        wrapperRef.current.clientHeight - 2 * canvasParentPaddingPixels
      );
    };

    recalc();

    // On different image size load or navbar open, recalc will happen
    const resizeObserver = new ResizeObserver(recalc);
    resizeObserver.observe(wrapperRef.current);

    return () => resizeObserver.disconnect();
  }, [windowWidth, windowHeight, image, largeCanvas]);

  useEffect(() => {
    if (image) {
      onImgLoad();
    }
  }, [windowHeight, windowWidth])

  useEffect(() => {
    onImgLoad();
  }, [viewOtherObjects])

  useEffect(() => {
    if (imageId !== null) {
      //@ts-ignore
      const { objpoly } = image;
      const coords = getCoordinates(objpoly);
      setViewOtherObjects(false);
      setZoomClip(calculateClip(coords));
      setFullB64('');
      setInitialB64('');
      setOtherObjectsLoaded(false);
    }
  }, [imageId])

  useEffect(() => {
    if (baseRecordId && image && imageId) {
      const reqClip = getClip();
      const isFullImage = reqClip[0] === 0 && reqClip[1] === 0 && reqClip[2] === image.width && reqClip[3] === image.height ? true : false;

      const { objpoly } = image;
      //@ts-ignore
      const coords = getCoordinates(objpoly);
      const initialClip = calculateClip(coords);
      const isInitialImage = reqClip[0] === initialClip[0] && reqClip[1] === initialClip[1] && reqClip[2] === initialClip[2] && reqClip[3] === initialClip[3] ? true : false;

      if (isFullImage && fullB64 !== '' && previousCanvasState === largeCanvas) {
        setB64(fullB64);
      } else if (isInitialImage && initialB64 !== '' && previousCanvasState === largeCanvas) {
        setB64(initialB64);
      }
      else if ((isFullImage || isInitialImage) || (isInitialImage && initialB64 === '') || (!isFullImage && !isInitialImage)) {
        setIsLoading(true);
        //@ts-ignore
        imgService.getObjImage(baseRecordPath, baseRecordId, imageId as number, image ? parseInt(image.width * getScaleFactor(image.width, image.height)) : maxImageWidth, reqClip).then(resp => {
          if (resp.success) {
            //@ts-ignore
            setB64(resp.data.image);
            if (isFullImage) {
              setFullB64(resp.data.image);
              setPreviousCanvasState(largeCanvas)
            }
            if (isInitialImage) {
              setInitialB64(resp.data.image);
              setPreviousCanvasState(largeCanvas)
            }
          }
        })
          .finally(() => {
            setIsLoading(false);
          });
      }
    }
  }, [zoomClip, largeCanvas])

  useEffect(() => {
    if (cropClip) {
      onDrawCropClip(cropClip);
    } else {
      onDrawCropClip(null);
    }

  }, [cropClip]);

  const onImgLoad = () => {
    let _ctx: CanvasRenderingContext2D | null = null;
    const hasCanvas = canvasRef && canvasRef.current;
    if (hasCanvas) {
      _ctx = canvasRef.current.getContext('2d');
    }
    if (_ctx === null) {
      return;
    }
    const hasImg = imgRef && imgRef.current;
    const ctx: CanvasRenderingContext2D = _ctx as CanvasRenderingContext2D;
    if (hasCanvas && hasImg) {

      //const scaleFactor = getScaleFactor(imgRef.current.width, imgRef.current.height)
      canvasRef.current.width = imgRef.current.width;
      canvasRef.current.height = imgRef.current.height;

      // ctx.drawImage(imgRef.current, 0, 0);

      if (viewOtherObjects && image) {
        if (otherObjectsLoaded) {
          otherObjects.forEach((r: DCRecord) => {
            drawPoint(ctx, imgRef.current!.width, imgRef.current!.height, r);
          });
        } else {
          imgService.getImageObjects(image.id as number)
            .then(resp => {
              if (resp.success) {
                setOtherObjects(resp.data || []);
                resp.data.forEach((r: DCRecord) => {
                  drawPoint(ctx, imgRef.current!.width, imgRef.current!.height, r);
                });
              }
            })
            .finally(() => {
              setOtherObjectsLoaded(true);
            });
        }
      } else {
        drawPolygon(ctx, imgRef.current.width, imgRef.current.height);
        drawPoint(ctx, imgRef.current.width, imgRef.current.height);
      }
    }
  }

  const getCoordinates = (wkt: string) => {
    let coords: Coordinates = null;
    try {
      const WKT = new OlFormatWKT();
      const geom = WKT.readGeometry(wkt);
      //@ts-ignore
      coords = geom.getCoordinates()[0];
    } catch (err) {
      // pass
    }
    return coords;
  }

  const getClip = () => {
    if (zoomClip !== null) {
      return zoomClip;
    } else if (image !== null) {
      return [0, 0, image.width, image.height] as Clip;
    } else {
      return [0, 0, 0, 0] as Clip;
    }
  }

  const calculateClip = (coords: Coordinates) => {
    // calculate based on polygon of the object (i.e. coords)

    //@ts-ignore
    const { width, height } = image;
    const ratio = height / width;

    if (coords) {
      let minX = width * 2;
      let minY = height * 2;
      let maxX = -minX;
      let maxY = -minY;
      coords.forEach(([x, y]) => {
        minX = Math.min(minX, x);
        minY = Math.min(minY, y);
        maxX = Math.max(maxX, x);
        maxY = Math.max(maxY, y);
      });

      const dx = maxX - minX;
      const dy = maxY - minY;
      let pad = Math.max(dx, dy);
      if (2 * pad < MIN_WIDTH) {
        pad = MIN_WIDTH - pad;
      }
      let bx = minX - pad / 2;
      let by = minY - pad / 2;
      let ex = maxX + pad / 2;
      let ey = maxY + pad / 2;

      //fix ratio
      const sizeX = ex - bx;
      const sizeY = ey - by;
      const maxSize = Math.max(sizeX, sizeY);
      if (maxSize === sizeX) {
        const correctHeight = sizeX * ratio;
        const padHeight = Math.floor((correctHeight - sizeY) / 2);
        by = by - padHeight;
        ey = ey + padHeight;
      } else if (maxSize === sizeY) {
        const correctWidth = sizeY / ratio;
        const padWidth = Math.floor((correctWidth - sizeX) / 2);
        bx = bx - padWidth;
        ex = ex + padWidth;
      }

      // pan rect inside the image 
      const incX = bx < 0 ? -bx : 0;
      const incY = by < 0 ? -by : 0;
      const decX = ex > width ? ex - width : 0;
      const decY = ey > height ? ey - height : 0;

      return [bx, by, ex, ey].map((n, i) => {
        if (i % 2 === 0) { // x 
          return Math.round(n + incX - decX);
        } else { // y
          return Math.round(n + incY - decY);
        }
      }) as Clip;

    }

    // else: calculate based on red dot

    //@ts-ignore
    const { objpx, objpy } = image;

    if (objpx && objpy) {
      const sz = width / 8;
      const bx = objpx - sz;
      const by = objpy - sz * ratio;
      const ex = objpx + sz;
      const ey = objpy + sz * ratio;

      // pan rect inside the image 
      const incX = bx < 0 ? -bx : 0;
      const incY = by < 0 ? -by : 0;
      const decX = ex > width ? ex - width : 0;
      const decY = ey > height ? ey - height : 0;

      return [bx, by, ex, ey].map((n, i) => {
        if (i % 2 === 0) { // x 
          return Math.round((n + incX - decX));
        } else { // y
          return Math.round((n + incY - decY));
        }
      }) as Clip;
    }

    // else: take the whole image
    return [0, 0, width, height] as Clip;
  }

  const viewOtherObjectsOnImage = () => {
    setViewOtherObjects(prevState => !prevState);
  }

  const handleCanvasClick = (coords: Point) => {
    if (image === null) {
      return;
    }
    const [nx, ny] = coords;
    const reqClip = getClip();
    const [bx, by, ex, ey] = reqClip;

    // original image width and height
    const w = image.width as number;
    const h = image.height as number;
    const [wRatio, hRatio] = getRatio(reqClip, w, h);

    //container width and height
    const cw = w * getScaleFactor(w, h);
    const ch = h * getScaleFactor(w, h);
   
    const [wImgRatio, hImgRatio] = getRatio(reqClip, cw, ch);
    
    const x = nx / wImgRatio + bx;
    const y = ny / hImgRatio + by;

    otherObjects.forEach(obj => {
      const { objpx, objpy } = obj;
      const u1 = ((objpx as number) - bx) * wRatio;
      const v1 = ((objpy as number) - by) * hRatio;

      const u2 = (x - bx) * wRatio;
      const v2 = (y - by) * hRatio;

      // const ratioPtRadius = PT_RADIUS;
      const ratioPtRadius = PT_RADIUS * (w as number) / cw;

      if (Math.abs(u2 - u1) < ratioPtRadius && Math.abs(v2 - v1) < ratioPtRadius) {
        window.open(`/object-analysis?object_id=${obj.objekt_id}&image_id=${image.id}`, '_blank');
      }
    })
  }

  const getRatio = (clip: Clip, w: number, h: number) => {
    const [bx, by, ex, ey] = clip;
    const width = ex - bx;
    const height = ey - by;
    const wRatio = w / width;
    const hRatio = h / height;
    return [wRatio, hRatio] as Ratio;
  }

  const drawPolygon = (ctx: CanvasRenderingContext2D, w: number, h: number) => {
    //@ts-ignore
    const { objpoly, /*width, height*/ } = image;

    ctx.beginPath();
    ctx.lineWidth = 2;
    ctx.strokeStyle = "#00FF00";

    if (image === null || !image.objpoly) {
      return;
    }
    const coords = getCoordinates(objpoly);
    if (!coords) {
      return
    }
    // const clip = calculateClip(coords);
    const reqClip = getClip();
    const [bx, by, ex, ey] = reqClip;
    //@ts-ignore
    const [wRatio, hRatio] = getRatio(reqClip, w, h);

    for (let i = 0; i < coords.length; ++i) {
      const [_u, _v] = coords[i];
      const u = (_u - bx) * wRatio;
      const v = (_v - by) * hRatio;
      if (i === 0) {
        ctx.moveTo(u, v);
      }
      // ctx.setLineDash([5, 15]);
      ctx.lineTo(u, v);
    }

    ctx.closePath();
    ctx.stroke();
  }

  const drawPoint = (ctx: CanvasRenderingContext2D, w: number, h: number, img?: DCRecord | null) => {
    //@ts-ignore
    const { objpx, objpy } = (img ? img : image);

    const reqClip = getClip();
    const [bx, by, ex, ey] = reqClip;

    const [wRatio, hRatio] = getRatio(reqClip, w, h);

    ctx.beginPath();
    ctx.lineWidth = 1;
    ctx.strokeStyle = img ? "#00FF00" : "#FF0000";
    ctx.fillStyle = img ? "#00FF00" : "#FF0000";

    const u = (objpx - bx) * wRatio;
    const v = (objpy - by) * hRatio;
    ctx.arc(u, v, PT_RADIUS, 0, 2 * Math.PI);

    ctx.fill();
  }

  const handleZoomInitial = () => {
    if (imageId !== null) {
      //@ts-ignore
      const { objpoly } = image;
      const coords = getCoordinates(objpoly);

      setZoomClip(calculateClip(coords));
    }
  }

  const handleZoomOut = () => {
    if (image) {
      setZoomClip([0, 0, image.width as number, image.height as number]);
    }
  }

const getScaleFactor = (width: number, height: number) => {
  let scaleFactor = 1;

  if (width > maxImageWidth) {
    // Case 1: shrink width
    scaleFactor = maxImageWidth / width;
    if (height * scaleFactor > maxImageHeight) {
      scaleFactor *= maxImageHeight / (height * scaleFactor);
    }
  } 
  else if (height > maxImageHeight) {
    // Case 2: shrink height
    scaleFactor = maxImageHeight / height;
    if (width * scaleFactor > maxImageWidth) {
      scaleFactor *= maxImageWidth / (width * scaleFactor);
    }
  } 
  else if (width < maxImageWidth) {
    // Case 3: upscale width
    scaleFactor = maxImageWidth / width;
    if (height * scaleFactor > maxImageHeight) {
      scaleFactor *= maxImageHeight / (height * scaleFactor);
    }
  } 
  else if (height < maxImageHeight) {
    // Case 4: upscale height
    scaleFactor = maxImageHeight / height;
    if (width * scaleFactor > maxImageWidth) {
      scaleFactor *= maxImageWidth / (width * scaleFactor);
    }
  }

  return scaleFactor;
};


const calculateRealCoordinates = (
  x: number,
  y: number,
  w: number,
  h: number,
  currentZoomClip: Clip,
  img: DCRecord,
  enforceAspectRatio: boolean = true
) => {
  if (!(currentZoomClip && img && img.width && img.height)) {
    return null;
  }

  const [bx, by, ex, ey] = currentZoomClip;
  const clip_width = ex - bx;
  const clip_height = ey - by;

  const width = img.width as number;
  const height = img.height as number;

  const aspectRatio = width / height;
  const scaleFactor = getScaleFactor(width, height);

  // Box in canvas space
  let zoom_start_x = Math.floor(x);
  let zoom_start_y = Math.floor(y);
  let zoom_end_x = Math.floor(x + w);
  let zoom_end_y = Math.floor(y + h);

  // Clip-relative
  let clip_start_x = Math.floor((zoom_start_x / (scaleFactor * width)) * clip_width);
  let clip_start_y = Math.floor((zoom_start_y / (scaleFactor * height)) * clip_height);
  let clip_end_x = Math.floor((zoom_end_x / (scaleFactor * width)) * clip_width);
  let clip_end_y = Math.floor((zoom_end_y / (scaleFactor * height)) * clip_height);

  // Translate back to image space
  let real_start_x = bx + clip_start_x;
  let real_start_y = by + clip_start_y;
  let real_end_x = bx + clip_end_x;
  let real_end_y = by + clip_end_y;

  // Normalize order
  if (real_end_x < real_start_x) [real_start_x, real_end_x] = [real_end_x, real_start_x];
  if (real_end_y < real_start_y) [real_start_y, real_end_y] = [real_end_y, real_start_y];

  // Only enforce aspect ratio if needed (zoom)
  if (enforceAspectRatio) {
    let newWidth = real_end_x - real_start_x;
    let newHeight = real_end_y - real_start_y;
    let currentRatio = newWidth / newHeight;

    if (currentRatio > aspectRatio) {
      // too wide → adjust width
      newWidth = Math.round(newHeight * aspectRatio);
      real_end_x = real_start_x + newWidth;
    } else {
      // too tall → adjust height
      newHeight = Math.round(newWidth / aspectRatio);
      real_end_y = real_start_y + newHeight;
    }
  }

  // Clamp to image bounds (works for both zoom + crop)
  if (real_start_x < 0) {
    real_end_x -= real_start_x;
    real_start_x = 0;
  }
  if (real_start_y < 0) {
    real_end_y -= real_start_y;
    real_start_y = 0;
  }
  if (real_end_x > width) {
    const diff = real_end_x - width;
    real_start_x -= diff;
    real_end_x = width;
  }
  if (real_end_y > height) {
    const diff = real_end_y - height;
    real_start_y -= diff;
    real_end_y = height;
  }

  real_start_x = Math.max(0, real_start_x);
  real_start_y = Math.max(0, real_start_y);

  return [real_start_x, real_start_y, real_end_x, real_end_y] as Clip;
};


  const handleStartDrawing = () => {
    setCropClip(null);
  }

  const handleDrawZoomBox = (x: number, y: number, w: number, h: number) => {
    if (zoomClip && image) {
      const newZoomClip = calculateRealCoordinates(x, y, w, h, zoomClip, image);
      if (newZoomClip) {
        setZoomClip(newZoomClip);
      }
    }
  }

  const handleDrawCropBox = (x: number, y: number, w: number, h: number) => {
    if (zoomClip && image) {
      const newCropClip = calculateRealCoordinates(x, y, w, h, zoomClip, image, false);
      if (newCropClip) {
        setCropClip(newCropClip);
      }
    }
  }

  const zoomedOutFully = image
    && zoomClip
    && zoomClip[0] === 0
    && zoomClip[1] === 0
    && zoomClip[2] === image.width
    && zoomClip[3] === image.height;

  const isHeightLargerThanWidth = image && (image.height as number) > (image.width as number);

  const [containerWidth, containerHeight] = useMemo(() => {
    if (!image) return [0,0];
    return [(image.width as number) * getScaleFactor((image.width as number), (image.height as number)),(image.height as number) * getScaleFactor((image.width as number), (image.height as number))]
  }, [image])

  return (
    //#e0dcdc is the background color of the skeleton, unloaded images
    <div
    ref={wrapperRef} 
    style={{
      display: "flex", justifyContent: "center", alignItems: "center", aspectRatio: imgAspectRatio, width: isHeightLargerThanWidth ? 'auto' : "100%", height: isHeightLargerThanWidth ? "100%" : "auto"
    }}>
      <Paper elevation={0} sx={{
        p: canvasParentPaddingPixels + "px", background: "#e0dcdc",
        height:"100%", width: "100%", 
        position: "relative", display: "flex", justifyContent: "center", alignItems: "center", 
      }}>
        {isLoading ?
          <Skeleton variant="rectangular" width={'100%'} height={'100%'} animation="wave" />
          :
          (image && b64 && b64.length) ?
            <Fragment>
              <img
                ref={imgRef}
                src={`data:image/png;base64, ${b64}`}
                style={{
                  display: b64 ? "flex" : "none",
                  position: "absolute",
                  top: "10px",
                  left: "10px",
                  height: "calc( 100% - 20px )",
                  width: "calc( 100% - 20px )",
                  zIndex: 100
                }}
                onLoad={() => onImgLoad()}
              />
              {/* <canvas ref={canvasRef} /> */}
              <Canvas
                mouseUp={mouseUp}
                ref={canvasRef}
                baseDraw={onImgLoad}
                onStartDrawing={handleStartDrawing}
                onDrawZoomBox={handleDrawZoomBox}
                onDrawCropBox={handleDrawCropBox}
                onWheelDown={handleZoomOut}
                onCanvasClick={handleCanvasClick}
                viewOtherObjects={viewOtherObjects}
                drawClip={zoomClip}
                cropClip={drawCropClip}
                containerWidth={containerWidth}
                containerHeight={containerHeight}
                ratio={(image.width as number) / (image.height as number)}
                setIsDrawing={setIsDrawing}
              />
              <Tooltip enterDelay={1000} title={t("operations.toggle_other_objects") as string}>
                <Fab
                  //@ts-ignore
                  color={"primary"}
                  size="small"
                  sx={{
                    position: "absolute",
                    bottom: 16,
                    left: 16,
                    backgroundColor: viewOtherObjects ? "#00FF00" : null,
                    "&:hover": {
                      backgroundColor: viewOtherObjects ? "#32CD32" : null
                    }
                  }}
                  disableRipple
                  onClick={viewOtherObjectsOnImage}
                >
                  <MapsHomeWorkIcon />
                </Fab>
              </Tooltip>
              <Fab
                color="primary"
                size="small"
                sx={{ position: "absolute", top: 16, right: 16 }}
                onClick={handleZoomInitial}
              >
                {image.ordinal}
              </Fab>
              <Fab
                color="primary"
                size="small"
                sx={{ position: "absolute", bottom: 16, right: 16 }}
                onClick={handleZoomOut}
                disabled={Boolean(zoomedOutFully)}
              >
                <ZoomOutIcon />
              </Fab>
            </Fragment>
            :
            <Skeleton variant="rectangular" width={'100%'} height={'100%'} animation={false} />
        }
      </Paper>
    </div>
  )
}

export default ObjectAnalysisImage;